import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { CalendarDays, Pencil } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Breadcrumbs } from "../components/layout/Breadcrumbs";
import { PageTitle } from "../components/layout/PageTitle";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ErrorState } from "../components/ErrorState";
import { EventStatusBadge } from "../components/EventStatusBadge";
import { EventDashboardStatsPanel } from "../components/event/EventDashboardStats";
import { EnrolmentTable } from "../components/event/EnrolmentTable";
import { CsvImportPanel } from "../components/event/CsvImportPanel";
import { StaffManagementPanel } from "../components/event/StaffManagementPanel";
import { AssignmentsPanel } from "../components/event/AssignmentsPanel";
import { WhatsappTemplatePanel } from "../components/event/WhatsappTemplatePanel";
import { EventFormModal } from "../components/event/EventFormModal";
import { useAuth } from "../hooks/useAuth";
import { getEvent } from "../services/event.service";
import { getApiErrorMessage } from "../lib/apiClient";

type TabKey = "overview" | "import" | "whatsapp" | "staff" | "assignments";

export default function EventDetailPage(): JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const [tab, setTab] = useState<TabKey>("overview");
  const [editingEvent, setEditingEvent] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["events", eventId],
    queryFn: () => getEvent(eventId!),
    enabled: !!eventId,
  });

  if (!eventId) return <ErrorState message="Missing event id" />;

  const isManagerLevel = user?.role === "SUPER_ADMIN" || data?.myRoleInEvent === "MANAGER";

  const tabs: { key: TabKey; label: string; visible: boolean }[] = [
    { key: "overview", label: "Overview & Enrolments", visible: true },
    { key: "import", label: "Import CSV", visible: true },
    { key: "whatsapp", label: "WhatsApp Template", visible: !!isManagerLevel },
    { key: "staff", label: "Staff", visible: !!isManagerLevel },
    { key: "assignments", label: "Assignments", visible: user?.role === "SUPER_ADMIN" },
  ];

  return (
    <DashboardLayout>
      <Breadcrumbs items={[{ label: "Events", to: "/events" }, { label: data?.event.name ?? "Event" }]} />

      {isLoading && <LoadingSpinner />}
      {isError && <ErrorState message={getApiErrorMessage(error)} />}

      {data && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <PageTitle
                icon={CalendarDays}
                title={data.event.name}
                description={`${data.event.course?.name} - ${new Date(data.event.eventDate).toLocaleDateString()} - ${data.event.startTime} to ${data.event.endTime} - ${data.event.location}`}
              />
              {isManagerLevel && (
                <button
                  type="button"
                  className="mt-0.5 rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-600 dark:hover:text-slate-200"
                  onClick={() => setEditingEvent(true)}
                  aria-label={`Edit ${data.event.name}`}
                  title="Edit event"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div>
            <EventStatusBadge status={data.event.status} />
          </div>

          <div className="mt-6 border-b border-slate-200 dark:border-slate-700">
            <nav className="-mb-px flex gap-6">
              {tabs
                .filter((t) => t.visible)
                .map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={clsx(
                      "border-b-2 px-1 py-3 text-sm font-medium transition-colors duration-150",
                      tab === t.key
                        ? "border-brand-600 text-brand-700 dark:border-brand-500 dark:text-brand-400"
                        : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200",
                    )}
                  >
                    {t.label}
                  </button>
                ))}
            </nav>
          </div>

          <div className="mt-6 animate-fade-in">
            {tab === "overview" && (
              <div className="space-y-6">
                <EventDashboardStatsPanel eventId={eventId} />
                <EnrolmentTable eventId={eventId} />
              </div>
            )}
            {tab === "import" && <CsvImportPanel eventId={eventId} />}
            {tab === "whatsapp" && isManagerLevel && <WhatsappTemplatePanel eventId={eventId} />}
            {tab === "staff" && isManagerLevel && <StaffManagementPanel eventId={eventId} />}
            {tab === "assignments" && user?.role === "SUPER_ADMIN" && <AssignmentsPanel eventId={eventId} />}
          </div>

          {isManagerLevel && (
            <EventFormModal open={editingEvent} onClose={() => setEditingEvent(false)} initial={data.event} />
          )}
        </>
      )}
    </DashboardLayout>
  );
}
